import SVGIcon from "common/SVGIcon";
import moment from "moment-timezone";
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import { useDispatch } from "react-redux";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import PropTypes from "prop-types";

import ErrorText from "common/ErrorText";
import events from "js/events";
import social from "js/social";
import { HIRE_STATE } from "lib/constants";
import { applicationShape } from "lib/propTypes";
import { isMobile } from "lib/util";
import { hireSetState } from "redux/applications/actions";

const DATE_FORMAT = "MM/DD/YYYY";

const CandidateStartDate = ({ application, disableSelfReport }) => {
    const dispatch = useDispatch();
    const [date, setDate] = useState(
        application.hire && moment.utc(application.hire.date_employed),
    );
    const [error, setError] = useState();
    const [submitted, setSubmitted] = useState(!!application.hire_id);

    const minDate = moment().subtract(1, "months");
    const maxDate = moment().add(2, "months");

    function onSubmit() {
        const dateEmployed = moment(date, DATE_FORMAT, true);
        if (!date || !dateEmployed.isValid()) {
            setError(`Please enter a date in the format ${DATE_FORMAT}`);
            return;
        }

        if (dateEmployed.isBefore(minDate) || dateEmployed.isAfter(maxDate)) {
            setError("Please enter a valid date in the future.");
            return;
        }

        dispatch(
            hireSetState({
                state: HIRE_STATE.EMPLOYED,
                options: {
                    application_id: application.id,
                    date_employed: moment.utc(dateEmployed.format("YYYY-MM-DD")).toISOString(),
                },
                onSuccess: () => setSubmitted(true),
            }),
        );
    }

    function shareToSocial(event) {
        event.preventDefault();

        const target = event.currentTarget;
        const { network } = target.dataset;
        const source = "ws-ref";
        const medium = network;
        const campaign = "referral";

        const url =
            `https://${window.location.hostname}/jobs/welcome/` +
            `?utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign}`;

        events.track(`REFERRAL_INVITE_${network.toUpperCase()}`, campaign);

        social.showPopup(network, url, "");
    }

    if (disableSelfReport) {
        return (
            <div className="card-container">
                <div className="card card--feedback">
                    <div className="card__body">
                        <div>This company has disabled start date reporting through WorkStep.</div>
                    </div>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="card-container">
                <div className="card card--feedback">
                    <div className="card__body card__body--success">
                        <SVGIcon icon="success-circle" />
                        <p>
                            Congrats on the new job {application.user.first_name}!
                            {date && " We have your start date recorded as:"}
                        </p>
                        {date && (
                            <div className="candidate-start-date__date">
                                {date.format("MM/DD/YYYY")}
                            </div>
                        )}
                        <p className="next-step">
                            <strong>Next step:</strong> Let your network know about the new job!
                            <br />
                            Select your platform:
                        </p>
                        <div className="social-share">
                            <a
                                href="#"
                                className="share-facebook"
                                data-network="fb"
                                onClick={shareToSocial}
                            >
                                <FacebookIcon />
                            </a>
                            <a
                                href="#"
                                className="share-twitter"
                                data-network="tw"
                                onClick={shareToSocial}
                            >
                                <TwitterIcon />
                            </a>
                            <a
                                href="#"
                                className="share-linkedin"
                                data-network="li"
                                onClick={shareToSocial}
                            >
                                <LinkedInIcon />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card-container candidate-start-date">
            <div className="card card--feedback">
                <div className="card__body">
                    <h2>
                        What start date were you provided by the hiring manager at{" "}
                        {application.position.company_name}?
                    </h2>
                    <DatePicker
                        dateFormat="MM/dd/yyyy"
                        isClearable
                        maxDate={maxDate.toDate()}
                        minDate={minDate.toDate()}
                        onChange={(x) => setDate(x && moment(x))}
                        placeholderText={DATE_FORMAT}
                        popperModifiers={{ flip: { enabled: false } }}
                        popperPlacement="bottom-start"
                        selected={date && date.toDate()}
                        withPortal={isMobile()}
                    />
                    <ErrorText error={error} />
                </div>
            </div>
            <div className="card-container__footer">
                <button className="btn btn-primary btn-large" onClick={onSubmit}>
                    Submit
                </button>
            </div>
        </div>
    );
};

CandidateStartDate.propTypes = {
    application: applicationShape.isRequired,
    disableSelfReport: PropTypes.bool.isRequired,
};

export default CandidateStartDate;
